/**
 * Bulk-import spec for the `Event` sheet of the organizer workbook.
 *
 * Unlike the row-per-entity sheets (Locations, Sessions, etc.), the Event
 * sheet describes a SINGLE event using a vertical `field, value` shape.
 * Each row is one key/value pair; the validator collapses every row into
 * a single parsed entity payload (returned identically per row so the
 * existing resolve/write pipeline still iterates a row list).
 *
 * Why KV instead of a single columnar row: Event has a longish field set
 * plus multi-FAQ pairs, and reviewing one event vertically reads much
 * better than scrolling sideways through many columns.
 *
 * Special handling:
 *   - "FAQ - question N" / "FAQ - answer N" pairs are aggregated into a
 *     `faqs: { question, answer }[]` field (1-indexed in the workbook).
 *     FAQ answers may include links using `[label](https://example.com)`.
 *   - `venueLabel`, `venueAddress` are surfaced as plain strings; the
 *     event's `venueLocationId` is wired up by the bulk callable, which
 *     either reuses an existing eventLocation with the same label or
 *     creates a new one in the same write.
 *   - `heroImage` is a path relative to the seed image set; null on
 *     import preserves the existing event hero or leaves it null.
 */
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { normalize } from "./importTypes";

export type EventStatusValue = "draft" | "published" | "archived";

const ALLOWED_STATUSES: readonly EventStatusValue[] = [
  "draft",
  "published",
  "archived",
] as const;

/** Match "YYYY-MM-DD". */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One row in the workbook: a single `{ field, value }` pair. Every data
 * row in the sheet looks like this (vertical layout — `validate` accepts
 * one row but the resolve/write pipeline aggregates them into one event
 * payload via `collapseEventRows` before sending to the callable).
 */
export interface RawEventImportRow extends Record<string, string | undefined> {
  field?: string;
  value?: string;
}

/**
 * Per-row parsed shape — keys mirror the raw cells. The collapse step
 * (`collapseEventRows`) produces the actual `ParsedEventImport` from a
 * list of these. The bulk pipeline still iterates rows so we report row
 * provenance on every parsed pair.
 */
export interface ParsedEventImportRow {
  field: string;
  value: string;
}

/**
 * Aggregate Event payload built by `collapseEventRows` from the per-row
 * cells. The bulk-create callable accepts a list of these (one per
 * event, even though the spec sheet only carries one).
 */
export interface ParsedEventImport {
  name: string;
  description: string | null;
  startDate: string;
  endDate: string;
  timezone: string;
  status: EventStatusValue;
  venueLabel: string | null;
  venueAddress: string | null;
  heroImage: string | null;
  heroTitleOverride: string | null;
  faqs: Array<{ question: string; answer: string }>;
}

/** Tag a key as the question or answer half of an FAQ pair. */
function parseFaqKey(
  field: string,
): { kind: "question" | "answer"; index: number } | null {
  const match = /^FAQ\s*[-–—]\s*(question|answer)\s+(\d+)$/i.exec(field.trim());
  if (!match) return null;
  return {
    kind: match[1].toLowerCase() as "question" | "answer",
    index: Number.parseInt(match[2], 10),
  };
}

export const eventImportSpec: ImportSpec<
  RawEventImportRow,
  ParsedEventImportRow
> = {
  sheetName: "Event",
  purpose:
    "Single event metadata in vertical key/value layout. One sheet = one event. Add FAQ pairs as 'FAQ - question N' / 'FAQ - answer N' rows; FAQ answers may include [label](https://example.com) links.",
  columns: [
    { key: "field", note: "required - event field name (e.g. name, startDate, FAQ - question 1)" },
    { key: "value", note: "required - the value for this field; FAQ answers can use [label](https://example.com) links" },
  ],
  example: {
    field: "name",
    value: "Winter Summit 2026",
  },
  validate(raw, lineNumber) {
    const field = normalize(raw.field);
    const value = normalize(raw.value);

    const base: Omit<ParsedRow<ParsedEventImportRow>, "status" | "message"> = {
      lineNumber,
      data: { field, value },
    };

    if (!field) {
      // Blank rows are common as visual spacers in the operator's vertical
      // sheet (see row 12 of 02-event.xlsx). Treat them as invalid without
      // an error message so the collapse step can drop them silently.
      return invalid(base, "Blank row");
    }
    // Empty value with a known field is an optional-field-omitted pattern
    // (e.g. row 11 of 02-event.xlsx for heroTitleOverride). Single source
    // of truth for "what's required" is `collapseEventRows` — it generates
    // diagnostics for any required field that's missing or malformed.
    return pending(base);
  },
};

/**
 * Collapse a list of validated key/value rows into one aggregated event
 * payload + per-row diagnostics. Returns null when required fields are
 * missing — the dialog surfaces those as row-level errors.
 *
 * Rows whose status is `invalid` are skipped (visual spacers). Unknown
 * fields produce one diagnostic each but don't block the collapse —
 * forward-compat for fields the operator might add later.
 */
export function collapseEventRows(rows: ParsedRow<ParsedEventImportRow>[]): {
  payload: ParsedEventImport | null;
  diagnostics: Array<{ lineNumber: number; message: string }>;
} {
  const diagnostics: Array<{ lineNumber: number; message: string }> = [];
  const map = new Map<string, { value: string; lineNumber: number }>();
  const faqs = new Map<
    number,
    { question?: string; answer?: string; lineNumber: number }
  >();

  for (const row of rows) {
    if (row.status !== "pending") continue;
    const { field, value } = row.data;
    const faqKey = parseFaqKey(field);
    if (faqKey) {
      const slot = faqs.get(faqKey.index) ?? { lineNumber: row.lineNumber };
      slot[faqKey.kind] = value;
      faqs.set(faqKey.index, slot);
      continue;
    }
    map.set(field, { value, lineNumber: row.lineNumber });
  }

  const name = map.get("name")?.value;
  const startDate = map.get("startDate")?.value;
  const endDate = map.get("endDate")?.value;
  const timezone = map.get("timezone")?.value;
  const statusRaw = map.get("status")?.value ?? "draft";

  if (!name) {
    diagnostics.push({
      lineNumber: 2,
      message: "Required field 'name' is missing.",
    });
  }
  if (!startDate || !ISO_DATE_PATTERN.test(startDate)) {
    diagnostics.push({
      lineNumber: map.get("startDate")?.lineNumber ?? 2,
      message: "Required field 'startDate' must be YYYY-MM-DD.",
    });
  }
  if (!endDate || !ISO_DATE_PATTERN.test(endDate)) {
    diagnostics.push({
      lineNumber: map.get("endDate")?.lineNumber ?? 2,
      message: "Required field 'endDate' must be YYYY-MM-DD.",
    });
  }
  if (!timezone) {
    diagnostics.push({
      lineNumber: 2,
      message: "Required field 'timezone' is missing.",
    });
  }
  if (
    statusRaw &&
    !(ALLOWED_STATUSES as readonly string[]).includes(statusRaw)
  ) {
    diagnostics.push({
      lineNumber: map.get("status")?.lineNumber ?? 2,
      message: `Invalid status "${statusRaw}". Must be one of: ${ALLOWED_STATUSES.join(", ")}`,
    });
  }
  if (startDate && endDate && endDate < startDate) {
    diagnostics.push({
      lineNumber: map.get("endDate")?.lineNumber ?? 2,
      message: "endDate must be on or after startDate.",
    });
  }

  // FAQ pairs: any half without its sibling becomes a diagnostic.
  const sortedFaqIndices = Array.from(faqs.keys()).sort((a, b) => a - b);
  const builtFaqs: Array<{ question: string; answer: string }> = [];
  for (const idx of sortedFaqIndices) {
    const slot = faqs.get(idx)!;
    if (!slot.question || !slot.answer) {
      diagnostics.push({
        lineNumber: slot.lineNumber,
        message: `FAQ pair ${idx} is missing a ${slot.question ? "answer" : "question"}.`,
      });
      continue;
    }
    builtFaqs.push({ question: slot.question, answer: slot.answer });
  }

  if (
    !name ||
    !startDate ||
    !ISO_DATE_PATTERN.test(startDate) ||
    !endDate ||
    !ISO_DATE_PATTERN.test(endDate) ||
    !timezone
  ) {
    return { payload: null, diagnostics };
  }

  const payload: ParsedEventImport = {
    name,
    description: map.get("description")?.value ?? null,
    startDate,
    endDate,
    timezone,
    status: (statusRaw as EventStatusValue) || "draft",
    venueLabel: map.get("venueLabel")?.value ?? null,
    venueAddress: map.get("venueAddress")?.value ?? null,
    heroImage: map.get("heroImage")?.value ?? null,
    heroTitleOverride: map.get("heroTitleOverride")?.value ?? null,
    faqs: builtFaqs,
  };

  return { payload, diagnostics };
}
